import React, { useState, useEffect, useRef } from 'react';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { Box, Button, TextField, InputAdornment } from '@mui/material';
import { Iconify } from 'minimal-ui';
import "../common/commonStyles.css";
import {
  subDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  format
} from 'date-fns';

export default function CommonDatePicker({ onChange, value: controlledValue, style, className, ...props }) {
  const today = new Date();
  const [value, setValue] = useState(
    Array.isArray(controlledValue) ? controlledValue : [null, null]
  );
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(Array.isArray(controlledValue) ? controlledValue : [null, null]);
    }
  }, [controlledValue]);

  const handleChange = (newValue) => {
    const safeValue = newValue || [null, null];
    setValue(safeValue);
    if (Array.isArray(safeValue) && safeValue[0] && safeValue[1]) {
      onChange?.(safeValue);
    }
  };

  const handleRangeClick = (rangeDates) => {
    setValue(rangeDates);
    setOpen(false);
    onChange?.(rangeDates);
  };

  const weekOptions = { weekStartsOn: 1 };

  const customRanges = [
    {
      label: 'Yesterday',
      value: [subDays(today, 1), subDays(today, 1)]
    },
    {
      label: 'This Week',
      value: [startOfWeek(today, weekOptions), endOfWeek(today, weekOptions)]
    },
    {
      label: 'This Month',
      value: [startOfMonth(today), endOfMonth(today)]
    },
    {
      label: 'Last Month',
      value: [
        startOfMonth(subDays(startOfMonth(today), 1)),
        endOfMonth(subDays(startOfMonth(today), 1))
      ]
    }
  ];

  const rangeButtonSx = {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    color: '#1976d2',
    borderRadius: '8px',
    padding: '4px 10px',
    fontSize: '12px',
    textTransform: 'none',
    flex: '1 1 auto',
    minWidth: 0,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      borderColor: '#1976d2',
      transform: 'translateY(-1px)'
    }
  };

  const displayValue = value && Array.isArray(value) && value[0] && value[1] 
    ? `${format(value[0], 'dd/MM/yyyy')} – ${format(value[1], 'dd/MM/yyyy')}`
    : '';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box 
        sx={{ 
          width: style?.width || '100%', 
          position: 'relative',
          '& .MuiMultiInputDateRangeField-root': {
            display: 'none !important'
          },
          '& .MuiLicenseInfo-root': {
            display: 'none !important'
          },
          '& [class*="MuiLicenseInfo"]': {
            display: 'none !important'
          },
          '& [class*="license"]': {
            display: 'none !important'
          }
        }}
        style={style}
        ref={anchorRef}
      >
        <TextField
          fullWidth
          size="small"
          value={displayValue}
          placeholder="Select Date"
          onClick={() => setOpen(true)}
          className={className}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Iconify icon="solar:calendar-mark-bold-duotone" width={20} sx={{ color: '#666666', cursor: 'pointer' }} />
              </InputAdornment>
            ),
            sx: {
              '&::before': {
                display: 'none !important'
              },
              '&::after': {
                display: 'none !important'
              }
            }
          }}
          sx={{
            '& .MuiInputBase-root': {
              '&::before': {
                display: 'none !important'
              },
              '&::after': {
                display: 'none !important'
              }
            },
            '& .MuiInput-underline': {
              '&::before': {
                display: 'none !important'
              },
              '&::after': {
                display: 'none !important'
              }
            },
            // '& input': {
            //   padding: '8px 12px'
            // }
          }}
        />
        <DateRangePicker
          calendars={1}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={Array.isArray(value) ? value : [null, null]}
          onChange={handleChange}
          slotProps={{
            textField: {
              sx: { display: 'none' }
              
            },
            actionBar: {
              actions: []
            },
            popper: {
              anchorEl: anchorRef.current,
              placement: 'bottom-start',
              sx: {
                '& .MuiPaper-root': {
                  borderRadius: '12px',
                  boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.2)',
                  '& .MuiLicenseInfo-root': {
                    display: 'none !important'
                  },
                  '& [class*="MuiLicenseInfo"]': {
                    display: 'none !important'
                  },
                  '& [class*="license"]': {
                    display: 'none !important'
                  }
                }
              }
            }
          }}
            slots={{
              layout: (props) => {
                const { children, ...other } = props;
                return (
                  <Box {...other}>
                    {children}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: '8px',
                        padding: '12px 16px',
                        borderTop: '1px solid #e0e0e0',
                        marginTop: '8px',
                        justifyContent: 'center'
                      }}
                    >
                      {customRanges.map((range) => (
                        <Button
                          key={range.label}
                          onClick={() => handleRangeClick(range.value)}
                          sx={rangeButtonSx}
                        >
                          {range.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                );
              }
            }}
          {...props}
        />
      </Box>
    </LocalizationProvider>
  );
}
